import * as i0 from '@angular/core';
import { EventEmitter, Output, Input, ViewEncapsulation, Component, NgModule } from '@angular/core';
import { NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { SizeToIconSizePipe, transformIconSizeToSize } from '@engineering11/ui-lib/e11-ui-config';

class E11PanelComponent {
    constructor() {
        /**
         * The size of the panel.
         */
        this.size = 'full';
        /**
         * The text of the header.
         */
        this.headerText = '';
        /**
         * The size of the header.
         */
        this.headerSize = 'h3';
        /**
         * The background color of the header.
         */
        this.headerBgColor = 'e11-bg-skin-primary';
        /**
         * The font color of the header.
         */
        this.headerFontColor = '';
        /**
         * The style of the header.
         */
        this.headerStyle = 'rounded-top';
        /**
         * The icon of the call-to-action.
         */
        this.ctaIcon = '';
        /**
         * The type of the call-to-action icon.
         */
        this.ctaIconType = undefined; // Will default to filled
        /**
         * Use `Size` values (e.g. sm, md...) to set the icon size.
         * `IconSize` still works but is deprecated and will be removed in the future.
         */
        this.ctaIconSize = 'md';
        /**
         * The text of the call-to-action.
         */
        this.ctaText = undefined;
        /**
         * The font color of the call-to-action.
         */
        this.ctaFontColor = undefined;
        /**
         * Whether the panel has a border.
         */
        this.hasBorder = false;
        /**
         * Whether the panel has a shadow.
         */
        this.hasShadow = false;
        /**
         * Class overrides for the content.
         */
        this.contentClassOverrides = 'e11-p-4'; // default padding
        /**
         * The template for the header.
         */
        this.headerTemplate = null;
        /**
         * The opacity of the background.
         */
        this.bgOpacity = 100;
        /**
         * Emits an event when the call-to-action is clicked.
         */
        this.ctaClicked = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "16.1.0", version: "18.2.13", type: E11PanelComponent, isStandalone: true, selector: "e11-panel", inputs: { size: "size", headerText: "headerText", headerSize: "headerSize", headerBgColor: "headerBgColor", headerFontColor: "headerFontColor", headerStyle: "headerStyle", ctaIcon: "ctaIcon", ctaIconType: "ctaIconType", ctaIconSize: ["ctaIconSize", "ctaIconSize", (value) => transformIconSizeToSize(value)], ctaText: "ctaText", ctaFontColor: "ctaFontColor", hasBorder: "hasBorder", hasShadow: "hasShadow", containerClassOverrides: "containerClassOverrides", contentClassOverrides: "contentClassOverrides", headerTemplate: "headerTemplate", bgOpacity: "bgOpacity" }, outputs: { ctaClicked: "ctaClicked" }, ngImport: i0, template: `
    <div
      class="e11-panel e11-mb-8 {{ containerClassOverrides }}"
      [style.background-color]="'hsla(0, 0%, 100%,' + bgOpacity / 100 + ')'"
      [ngClass]="{
        'e11-w-[20%]': size === 'xs',
        'e11-w-[30%]': size === 'sm',
        'e11-w-[50%]': size === 'md',
        'e11-w-[60%]': size === 'lg',
        'e11-w-[80%]': size === 'xl',
        'e11-w-full': size === 'full' || ''
      }"
    >
      <div
        *ngIf="headerText || headerTemplate"
        class="e11-panel-title e11-flex e11-items-center e11-px-4 e11-py-3 {{ headerBgColor }}"
        [ngClass]="{
          'e11-rounded-t-lg': headerStyle === 'rounded-top',
          'e11-rounded-none': headerStyle === 'square',
          'e11-rounded-lg': headerStyle === 'rounded'
        }"
      >
        <ng-container *ngIf="headerTemplate" [ngTemplateOutlet]="headerTemplate"></ng-container>
        <ng-container *ngIf="!headerTemplate">
          <div class="e11-grow">
            <h1 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h1'">{{ headerText }}</h1>
            <h2 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h2'">{{ headerText }}</h2>
            <h3 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h3'">{{ headerText }}</h3>
            <h4 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h4' || ''">{{ headerText }}</h4>
            <h5 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h5'">{{ headerText }}</h5>
            <h6 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h6'">{{ headerText }}</h6>
          </div>
          <div class="e11-flex e11-items-center e11-justify-end {{ headerFontColor }}">
            <span
              *ngIf="ctaText || ctaIcon"
              class="e11-flex e11-items-center e11-text-sm e11-justify-end e11-cursor-pointer hover:e11-brightness-50 {{ ctaFontColor }}"
              (click)="this.ctaClicked.emit()"
            >
              <span [ngClass]="ctaIconType ? 'material-icons-' + ctaIconType : 'material-icons'" class="{{ ctaIconSize | sizeToIconSize }} e11-mr-1">
                {{ ctaIcon }}
              </span>
              {{ ctaText }}
            </span>
          </div>
        </ng-container>
      </div>

      <div class="e11-panel-content {{ contentClassOverrides }}">
        <ng-content></ng-content>
      </div>
    </div>
  `, isInline: true, styles: [".e11-panel .e11-panel-title h1,.e11-panel .e11-panel-title h2,.e11-panel .e11-panel-title h3,.e11-panel .e11-panel-title h4,.e11-panel .e11-panel-title h5,.e11-panel .e11-panel-title h6{line-height:100%}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: SizeToIconSizePipe, name: "sizeToIconSize" }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-panel', standalone: true, imports: [NgClass, NgIf, NgTemplateOutlet, SizeToIconSizePipe], template: `
    <div
      class="e11-panel e11-mb-8 {{ containerClassOverrides }}"
      [style.background-color]="'hsla(0, 0%, 100%,' + bgOpacity / 100 + ')'"
      [ngClass]="{
        'e11-w-[20%]': size === 'xs',
        'e11-w-[30%]': size === 'sm',
        'e11-w-[50%]': size === 'md',
        'e11-w-[60%]': size === 'lg',
        'e11-w-[80%]': size === 'xl',
        'e11-w-full': size === 'full' || ''
      }"
    >
      <div
        *ngIf="headerText || headerTemplate"
        class="e11-panel-title e11-flex e11-items-center e11-px-4 e11-py-3 {{ headerBgColor }}"
        [ngClass]="{
          'e11-rounded-t-lg': headerStyle === 'rounded-top',
          'e11-rounded-none': headerStyle === 'square',
          'e11-rounded-lg': headerStyle === 'rounded'
        }"
      >
        <ng-container *ngIf="headerTemplate" [ngTemplateOutlet]="headerTemplate"></ng-container>
        <ng-container *ngIf="!headerTemplate">
          <div class="e11-grow">
            <h1 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h1'">{{ headerText }}</h1>
            <h2 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h2'">{{ headerText }}</h2>
            <h3 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h3'">{{ headerText }}</h3>
            <h4 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h4' || ''">{{ headerText }}</h4>
            <h5 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h5'">{{ headerText }}</h5>
            <h6 class="e11-truncate {{ headerFontColor }}" *ngIf="headerSize === 'h6'">{{ headerText }}</h6>
          </div>
          <div class="e11-flex e11-items-center e11-justify-end {{ headerFontColor }}">
            <span
              *ngIf="ctaText || ctaIcon"
              class="e11-flex e11-items-center e11-text-sm e11-justify-end e11-cursor-pointer hover:e11-brightness-50 {{ ctaFontColor }}"
              (click)="this.ctaClicked.emit()"
            >
              <span [ngClass]="ctaIconType ? 'material-icons-' + ctaIconType : 'material-icons'" class="{{ ctaIconSize | sizeToIconSize }} e11-mr-1">
                {{ ctaIcon }}
              </span>
              {{ ctaText }}
            </span>
          </div>
        </ng-container>
      </div>

      <div class="e11-panel-content {{ contentClassOverrides }}">
        <ng-content></ng-content>
      </div>
    </div>
  `, encapsulation: ViewEncapsulation.None, styles: [".e11-panel .e11-panel-title h1,.e11-panel .e11-panel-title h2,.e11-panel .e11-panel-title h3,.e11-panel .e11-panel-title h4,.e11-panel .e11-panel-title h5,.e11-panel .e11-panel-title h6{line-height:100%}\n"] }]
        }], propDecorators: { size: [{
                type: Input
            }], headerText: [{
                type: Input
            }], headerSize: [{
                type: Input
            }], headerBgColor: [{
                type: Input
            }], headerFontColor: [{
                type: Input
            }], headerStyle: [{
                type: Input
            }], ctaIcon: [{
                type: Input
            }], ctaIconType: [{
                type: Input
            }], ctaIconSize: [{
                type: Input,
                args: [{ transform: (value) => transformIconSizeToSize(value) }]
            }], ctaText: [{
                type: Input
            }], ctaFontColor: [{
                type: Input
            }], hasBorder: [{
                type: Input
            }], hasShadow: [{
                type: Input
            }], containerClassOverrides: [{
                type: Input
            }], contentClassOverrides: [{
                type: Input
            }], headerTemplate: [{
                type: Input
            }], bgOpacity: [{
                type: Input
            }], ctaClicked: [{
                type: Output
            }] } });

class E11PanelModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PanelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11PanelModule, imports: [E11PanelComponent], exports: [E11PanelComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PanelModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PanelModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11PanelComponent],
                    exports: [E11PanelComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11PanelComponent, E11PanelModule };
